import { ArrowUpRight, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "./cart";
import { Reveal } from "./Reveal";
import { PRODUCTS, PRICE_NOTE, mailto, whatsapp } from "./data";

const Price = ({ p }) => (
  <div data-testid={`product-price-${p.id}`} className="mt-5 flex flex-wrap items-end justify-between gap-x-6 gap-y-2 border-t border-[#FDFBF7]/20 pt-4">
    <span className="text-xs uppercase tracking-[0.2em] text-[#F4EFE6]/85">{p.spec}</span>
    <span className="font-serif text-[#E8893F]">
      <span className={p.unit ? "text-3xl sm:text-4xl" : "text-xl italic"}>{p.price}</span>
      {p.unit && <span className="ml-1 text-sm text-[#F4EFE6]/80">{p.unit}</span>}
    </span>
  </div>
);

const LAYOUT = {
  casa: "lg:col-span-7 lg:row-span-2 min-h-[460px] lg:min-h-[640px]",
  bancali: "lg:col-span-5 min-h-[400px] lg:min-h-0",
  negozi: "lg:col-span-5 min-h-[400px] lg:min-h-0",
  aziende: "lg:col-span-12 min-h-[340px] lg:min-h-[300px]",
};

const AddBtn = ({ p }) => {
  const { add, setOpen } = useCart();
  if (!p.cartId) return null;
  return (
    <button
      onClick={() => { add(p.cartId); toast.success(`${p.title} aggiunto al carrello`, { action: { label: "Vedi carrello", onClick: () => setOpen(true) } }); }}
      data-testid={`product-add-to-cart-${p.id}`}
      className="inline-flex items-center gap-2 rounded-full bg-[#FDFBF7] text-[#1A110C] px-5 py-3 text-sm font-semibold hover:bg-[#E8893F] transition-colors"
    >
      <ShoppingBag className="w-4 h-4" /> Aggiungi al carrello
    </button>
  );
};

const Card = ({ p }) => (
  <article data-testid={`product-card-${p.id}`} className="group relative h-full overflow-hidden rounded-3xl bg-[#1A110C] text-[#FDFBF7]">
    <img src={p.img} alt={p.title} className="absolute inset-0 w-full h-full object-cover opacity-70 transition-transform duration-[1.4s] ease-out group-hover:scale-110" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#1A110C] via-[#1A110C]/60 to-[#1A110C]/10" />
    <div className="relative h-full flex flex-col justify-between p-7 sm:p-9">
      <div className="flex items-start justify-between">
        <span className="rounded-full border border-[#FDFBF7]/30 backdrop-blur-md bg-[#1A110C]/30 px-3.5 py-1.5 text-[11px] uppercase tracking-[0.2em]">{p.tag}</span>
        <span className="font-serif italic text-3xl text-[#E8893F]">{p.n}</span>
      </div>
      <div className="max-w-lg">
        <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.02] tracking-tight">{p.title}</h3>
        <p className="mt-4 text-sm sm:text-base text-[#F4EFE6]/85 leading-relaxed">{p.text}</p>
        <Price p={p} />
        <div className="mt-6 flex flex-wrap gap-3">
        <AddBtn p={p} />
        <a
          href={mailto(`Richiesta preventivo — ${p.title}`)}
          data-testid={`product-quote-${p.id}`}
          className="inline-flex items-center gap-2 rounded-full bg-[#E8893F] text-[#1A110C] px-5 py-3 text-sm font-semibold hover:bg-[#FDFBF7] transition-colors"
        >
          Richiedi preventivo <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
        </a>
        <a
          href={whatsapp(`Ciao Pellet Italiana, vorrei un preventivo per: ${p.title} (${p.spec})`)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`product-whatsapp-${p.id}`}
          className="inline-flex items-center gap-2 rounded-full border border-[#FDFBF7]/40 px-5 py-3 text-sm font-semibold hover:border-[#25D366] hover:text-[#25D366] transition-colors"
        >
          WhatsApp
        </a>
        </div>
      </div>
    </div>
  </article>
);

export const Products = () => (
  <section id="prodotti" data-testid="products-section" className="bg-[#FDFBF7] text-[#2A1C15] pt-32 sm:pt-44 pb-24 sm:pb-36">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-8 mb-16 sm:mb-24 items-end">
        <Reveal className="lg:col-span-7">
          <p className="text-xs uppercase tracking-[0.3em] text-[#8C4A28] mb-5">La vetrina</p>
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            Ogni formato,<br /><em className="text-[#C9692C]">lo stesso abete.</em>
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5">
          <p className="text-base sm:text-lg text-[#2A1C15]/75 leading-relaxed">
            Dalla stufa di casa allo scaffale del negozio: confezioni di pellet in vero legno di abete per ogni esigenza. Scrivici o chiamaci per disponibilità.
          </p>
          <p data-testid="price-note" className="mt-3 text-xs uppercase tracking-[0.2em] text-[#8C4A28]">{PRICE_NOTE}</p>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 lg:auto-rows-[minmax(300px,auto)] gap-5 lg:gap-6">
        {PRODUCTS.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.08} className={LAYOUT[p.id]}>
            <Card p={p} />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
